import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Save, GripVertical, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// Platform-default column order for the Candidates tab (no decision/notes/shortlistedDate here)
const DEFAULT_CANDIDATES_ORDER = [
  'status', 'overallScore', 'resumeScore', 'assessmentScore', 'interviewScore',
  'proctoring', 'duplicates', 'documents', 'candidateDetails',
  'screening', 'companyName', 'storeId', 'storeName', 'storeRegion', 'storeCity', 'storeState', 'storeCategory', 'armName', 'armEmail',
  'cityCode', 'cityCity', 'cityState', 'cityRegion',
  'currentCtc', 'noticePeriod', 'designation', 'location', 'industry', 'experience',
  'experienceYears', 'latestEducation', 'skills', 'age', 'gender',
  'email', 'phone', 'social', 'appliedDate', 'completedAt',
];

// Platform-default column order for the Selected (shortlist) tab
// decision is placed first (primary action), notes is placed last (write column)
const DEFAULT_SHORTLIST_ORDER = [
  'decision',
  'overallScore', 'resumeScore', 'assessmentScore', 'interviewScore',
  'shortlistedDate', 'completedAt', 'appliedDate', 'phone', 'proctoring', 'duplicates', 'documents',
  'status', 'companyName', 'currentCtc', 'noticePeriod', 'designation', 'experience',
  'experienceYears', 'latestEducation', 'location', 'industry', 'skills', 'age', 'gender',
  'email', 'social', 'screening', 'storeId', 'storeName', 'storeRegion', 'storeCity', 'storeState', 'storeCategory', 'armName', 'armEmail',
  'cityCode', 'cityCity', 'cityState', 'cityRegion',
  'candidateDetails',
  'notes', // always last
];

// Platform-default column order for the Rejected tab
// decision is placed first (primary action), notes is placed last (write column)
const DEFAULT_REJECTED_ORDER = [
  'decision',
  'overallScore', 'resumeScore', 'assessmentScore', 'interviewScore',
  'rejectedDate', 'completedAt', 'appliedDate', 'phone', 'proctoring', 'duplicates', 'documents',
  'status', 'companyName', 'currentCtc', 'noticePeriod', 'designation', 'experience',
  'experienceYears', 'latestEducation', 'location', 'industry', 'skills', 'age', 'gender',
  'email', 'social', 'screening', 'storeId', 'storeName', 'storeRegion', 'storeCity', 'storeState', 'storeCategory', 'armName', 'armEmail',
  'cityCode', 'cityCity', 'cityState', 'cityRegion',
  'candidateDetails',
  'notes', // always last
];

// Keep legacy DEFAULT_ORDER for internal fallbacks
const DEFAULT_ORDER = DEFAULT_CANDIDATES_ORDER;

const SORT_OPTIONS = [
  { id: 'overallScore', name: 'Overall Score' },
  { id: 'resumeScore', name: 'Resume Score' },
  { id: 'assessmentScore', name: 'Assessment Score' },
  { id: 'interviewScore', name: 'Interview Score' },
  { id: 'appliedDate', name: 'Applied Date' },
  { id: 'completedAt', name: 'Completed Date' },
];

const SHORTLIST_SORT_OPTIONS = [
  ...SORT_OPTIONS,
  { id: 'shortlistedDate', name: 'Selected Date' },
];

const REJECTED_SORT_OPTIONS = [
  ...SORT_OPTIONS,
  { id: 'rejectedDate', name: 'Rejected Date' },
];

const BASE_LABELS = {
  status: 'Status',
  overallScore: 'Overall Score',
  resumeScore: 'Resume Score',
  assessmentScore: 'Assessment Score',
  interviewScore: 'Interview Score',
  proctoring: 'Proctoring',
  duplicates: 'Duplicates',
  shortlistedDate: 'Selected Date',
  rejectedDate: 'Rejected Date',
  documents: 'Documents',
  candidateDetails: 'Candidate Details',
  screening: 'Screening',
  companyName: 'Company Name',
  storeId: 'Store Code',
  storeName: 'Store Name',
  storeRegion: 'Store Region',
  storeCity: 'Store City',
  storeState: 'Store State',
  storeCategory: 'Store Category',
  armName: 'ARM Name',
  armEmail: 'ARM Email',
  cityCode: 'City Code',
  cityCity: 'City Name',
  cityState: 'City State',
  cityRegion: 'City Region',
  currentCtc: 'Current CTC',
  noticePeriod: 'Notice Period',
  designation: 'Designation',
  location: 'Location',
  industry: 'Industry',
  experience: 'Experience (Band)',
  experienceYears: 'Experience (Years)',
  latestEducation: 'Education',
  skills: 'Skills',
  age: 'Age',
  gender: 'Gender',
  email: 'Email',
  phone: 'Phone',
  social: 'Social Links',
  appliedDate: 'Applied Date',
  completedAt: 'Completed At',
  // Tab-specific columns
  decision: 'Decision',
  notes: 'Notes',
};

/**
 * The real platform defaults, mirrored from candidatesTableLayout.js.
 * Columns absent from this map default to true (e.g. custom fields).
 * Score columns are true because multi-method screenings show them;
 * layout code handles per-screening overrides automatically.
 */
const PLATFORM_DEFAULT_VISIBILITY = {
  // Always shown
  overallScore: true,
  resumeScore: true,
  assessmentScore: true,
  interviewScore: true,
  proctoring: true,
  duplicates: true,
  shortlistedDate: true,
  rejectedDate: true,
  completedAt: true,
  appliedDate: false,
  phone: true,
  documents: true,
  // Tab-specific — shown by default in their respective tabs
  decision: true,
  notes: true,
  // Hidden by default
  candidateDetails: false,
  screening: false,
  companyName: false,
  storeId: false,
  storeName: false,
  storeRegion: false,
  storeCity: false,
  storeState: false,
  storeCategory: false,
  armName: false,
  armEmail: false,
  cityCode: false,
  cityCity: false,
  cityState: false,
  cityRegion: false,
  currentCtc: false,
  noticePeriod: false,
  designation: false,
  location: false,
  industry: false,
  experience: false,
  experienceYears: false,
  latestEducation: false,
  skills: false,
  age: false,
  gender: false,
  email: false,
  social: false,
  status: false,
};

const TableLayoutAdmin = ({ orgId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasOverrides, setHasOverrides] = useState(false);
  
  // Candidates tab
  const [order, setOrder] = useState([]);
  const [visibility, setVisibility] = useState({});
  const [candidateSortColumn, setCandidateSortColumn] = useState('overallScore');
  const [candidateSortDirection, setCandidateSortDirection] = useState('desc');
  // Selected (shortlist) tab
  const [shortlistOrder, setShortlistOrder] = useState([]);
  const [shortlistVisibility, setShortlistVisibility] = useState({});
  const [shortlistSortColumn, setShortlistSortColumn] = useState('overallScore');
  const [shortlistSortDirection, setShortlistSortDirection] = useState('desc');
  // Rejected tab
  const [rejectedOrder, setRejectedOrder] = useState([]);
  const [rejectedVisibility, setRejectedVisibility] = useState({});
  const [rejectedSortColumn, setRejectedSortColumn] = useState('overallScore');
  const [rejectedSortDirection, setRejectedSortDirection] = useState('desc');
  // Excel export
  const [excelOrder, setExcelOrder] = useState([]);
  const [excelVisibility, setExcelVisibility] = useState({});
  const [activeTab, setActiveTab] = useState('ui');
  const [rawUiConfig, setRawUiConfig] = useState({});
  const [labels, setLabels] = useState({ ...BASE_LABELS });

  useEffect(() => {
    loadConfig();
  }, [orgId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const rawRes = await axios.get(`/api/internal/org-config/${orgId}/raw`);
      const rawConfig = rawRes.data?.config || {};
      setRawUiConfig(rawConfig.ui || {});
      const regFields = rawConfig.registrationFields || [];
      
      const customFieldIds = regFields
        .filter(f => !['name', 'email', 'phone'].includes(f.fieldId))
        .map(f => f.fieldId.startsWith('custom_') ? f.fieldId : `custom_${f.fieldId}`);
        
      const customLabels = {};
      regFields.forEach(f => {
        if (!['name', 'email', 'phone'].includes(f.fieldId)) {
          const key = f.fieldId.startsWith('custom_') ? f.fieldId : `custom_${f.fieldId}`;
          customLabels[key] = f.label || f.fieldId;
        }
      });
      setLabels(prev => ({ ...prev, ...customLabels }));

      // Candidates tab available columns (no decision/notes/shortlistedDate)
      const allCandidateColumns = [...DEFAULT_CANDIDATES_ORDER, ...customFieldIds];
      // Shortlist tab available columns (includes decision, notes, shortlistedDate + custom fields)
      const allShortlistColumns = [...DEFAULT_SHORTLIST_ORDER, ...customFieldIds
        .filter(id => !DEFAULT_SHORTLIST_ORDER.includes(id))];
      // Rejected tab available columns (includes decision, notes + custom fields)
      const allRejectedColumns = [...DEFAULT_REJECTED_ORDER, ...customFieldIds
        .filter(id => !DEFAULT_REJECTED_ORDER.includes(id))];
      // Excel columns (same as candidates)
      const allAvailableColumns = allCandidateColumns;

      const injectMissingStoreColumns = (validOrder, missingCols) => {
        const order = [...validOrder];
        const storeRegionIdx = order.indexOf('storeRegion');
        const storeColsToInject = ['storeCity', 'storeState', 'storeCategory'].filter(c => missingCols.includes(c));
        
        if (storeRegionIdx !== -1 && storeColsToInject.length > 0) {
          storeColsToInject.forEach((col, i) => {
            order.splice(storeRegionIdx + 1 + i, 0, col);
          });
        }
        
        const remainingMissing = missingCols.filter(c => !storeColsToInject.includes(c));
        return [...order, ...remainingMissing];
      };

      // ── Candidates tab layout ──────────────────────────────────────────────
      if (rawConfig.ui?.defaultCandidateColumnOrder) {
        setHasOverrides(true);
        const savedOrder = rawConfig.ui.defaultCandidateColumnOrder;
        const validSavedOrder = savedOrder.filter(c => allCandidateColumns.includes(c));
        const missing = allCandidateColumns.filter(c => !validSavedOrder.includes(c));
        setOrder(injectMissingStoreColumns(validSavedOrder, missing));
        const cleanVisibility = {};
        Object.keys(rawConfig.ui.defaultCandidateColumnVisibility || {}).forEach(k => {
          if (allCandidateColumns.includes(k)) cleanVisibility[k] = rawConfig.ui.defaultCandidateColumnVisibility[k];
        });
        setVisibility(cleanVisibility);
      } else {
        setHasOverrides(false);
        setOrder(allCandidateColumns);
        setVisibility({});
      }
      setCandidateSortColumn(rawConfig.ui?.defaultCandidateSortColumn || 'overallScore');
      setCandidateSortDirection(rawConfig.ui?.defaultCandidateSortDirection || 'desc');

      // ── Selected (shortlist) tab layout ────────────────────────────────────
      if (rawConfig.ui?.defaultShortlistColumnOrder) {
        const savedShortlistOrder = rawConfig.ui.defaultShortlistColumnOrder;
        const validShortlist = savedShortlistOrder.filter(c => allShortlistColumns.includes(c));
        const missingShortlist = allShortlistColumns.filter(c => !validShortlist.includes(c));
        setShortlistOrder(injectMissingStoreColumns(validShortlist, missingShortlist));
        const cleanShortlistVis = {};
        Object.keys(rawConfig.ui.defaultShortlistColumnVisibility || {}).forEach(k => {
          if (allShortlistColumns.includes(k)) cleanShortlistVis[k] = rawConfig.ui.defaultShortlistColumnVisibility[k];
        });
        setShortlistVisibility(cleanShortlistVis);
      } else {
        setShortlistOrder(allShortlistColumns);
        setShortlistVisibility({});
      }
      setShortlistSortColumn(rawConfig.ui?.defaultShortlistSortColumn || 'overallScore');
      setShortlistSortDirection(rawConfig.ui?.defaultShortlistSortDirection || 'desc');

      // ── Rejected tab layout ────────────────────────────────────────────────
      if (rawConfig.ui?.defaultRejectedColumnOrder) {
        const savedRejectedOrder = rawConfig.ui.defaultRejectedColumnOrder;
        const validRejected = savedRejectedOrder.filter(c => allRejectedColumns.includes(c));
        const missingRejected = allRejectedColumns.filter(c => !validRejected.includes(c));
        setRejectedOrder(injectMissingStoreColumns(validRejected, missingRejected));
        const cleanRejectedVis = {};
        Object.keys(rawConfig.ui.defaultRejectedColumnVisibility || {}).forEach(k => {
          if (allRejectedColumns.includes(k)) cleanRejectedVis[k] = rawConfig.ui.defaultRejectedColumnVisibility[k];
        });
        setRejectedVisibility(cleanRejectedVis);
      } else {
        setRejectedOrder(allRejectedColumns);
        setRejectedVisibility({});
      }
      setRejectedSortColumn(rawConfig.ui?.defaultRejectedSortColumn || 'overallScore');
      setRejectedSortDirection(rawConfig.ui?.defaultRejectedSortDirection || 'desc');

      // ── Excel export layout ────────────────────────────────────────────────
      if (rawConfig.ui?.defaultExcelColumnOrder) {
        const savedExcelOrder = rawConfig.ui.defaultExcelColumnOrder;
        const validSavedExcelOrder = savedExcelOrder.filter(c => allAvailableColumns.includes(c));
        const missingExcel = allAvailableColumns.filter(c => !validSavedExcelOrder.includes(c));
        setExcelOrder(injectMissingStoreColumns(validSavedExcelOrder, missingExcel));
        const cleanExcelVisibility = {};
        Object.keys(rawConfig.ui.defaultExcelColumnVisibility || {}).forEach(k => {
          if (allAvailableColumns.includes(k)) cleanExcelVisibility[k] = rawConfig.ui.defaultExcelColumnVisibility[k];
        });
        setExcelVisibility(cleanExcelVisibility);
      } else {
        setExcelOrder(allAvailableColumns);
        setExcelVisibility({});
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load table layout configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(order);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    setOrder(newOrder);
  };

  const handleShortlistDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(shortlistOrder);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    setShortlistOrder(newOrder);
  };

  const handleRejectedDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(rejectedOrder);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    setRejectedOrder(newOrder);
  };

  const handleExcelDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(excelOrder);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    setExcelOrder(newOrder);
  };

  const toggleExcelVisibility = (colId) => {
    setExcelVisibility(prev => {
      const platformDefault = PLATFORM_DEFAULT_VISIBILITY[colId] !== undefined ? PLATFORM_DEFAULT_VISIBILITY[colId] : true;
      const current = prev[colId] !== undefined ? prev[colId] : platformDefault;
      return { ...prev, [colId]: !current };
    });
  };

  const toggleVisibility = (colId) => {
    setVisibility(prev => {
      const current = prev[colId] !== undefined ? prev[colId] : true;
      return { ...prev, [colId]: !current };
    });
  };

  const toggleShortlistVisibility = (colId) => {
    setShortlistVisibility(prev => {
      const current = prev[colId] !== undefined ? prev[colId] : (PLATFORM_DEFAULT_VISIBILITY[colId] !== undefined ? PLATFORM_DEFAULT_VISIBILITY[colId] : true);
      return { ...prev, [colId]: !current };
    });
  };

  const toggleRejectedVisibility = (colId) => {
    setRejectedVisibility(prev => {
      const current = prev[colId] !== undefined ? prev[colId] : (PLATFORM_DEFAULT_VISIBILITY[colId] !== undefined ? PLATFORM_DEFAULT_VISIBILITY[colId] : true);
      return { ...prev, [colId]: !current };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const uiUpdate = {
        defaultCandidateColumnOrder: order,
        defaultCandidateColumnVisibility: visibility,
        defaultCandidateSortColumn: candidateSortColumn,
        defaultCandidateSortDirection: candidateSortDirection,
        defaultShortlistColumnOrder: shortlistOrder,
        defaultShortlistColumnVisibility: shortlistVisibility,
        defaultShortlistSortColumn: shortlistSortColumn,
        defaultShortlistSortDirection: shortlistSortDirection,
        defaultRejectedColumnOrder: rejectedOrder,
        defaultRejectedColumnVisibility: rejectedVisibility,
        defaultRejectedSortColumn: rejectedSortColumn,
        defaultRejectedSortDirection: rejectedSortDirection,
        defaultExcelColumnOrder: excelOrder,
        defaultExcelColumnVisibility: excelVisibility,
      };
      await axios.put(`/api/internal/org-config/${orgId}`, { ui: uiUpdate });
      toast.success('Table layout saved successfully');
      setHasOverrides(true);
    } catch (err) {
      toast.error('Failed to save table layout');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async (type) => {
    const labelMap = {
      ui: 'Candidates Table',
      shortlist: 'Selected Tab',
      rejected: 'Rejected Tab',
      excel: 'Excel Export',
    };
    if (!window.confirm(`Reset ${labelMap[type] || type} layout to platform defaults?`)) return;
    setSaving(true);
    try {
      const updatedUi = { ...rawUiConfig };
      if (type === 'ui') {
        updatedUi.defaultCandidateColumnOrder = null;
        updatedUi.defaultCandidateColumnVisibility = null;
        updatedUi.defaultCandidateSortColumn = null;
        updatedUi.defaultCandidateSortDirection = null;
      } else if (type === 'shortlist') {
        updatedUi.defaultShortlistColumnOrder = null;
        updatedUi.defaultShortlistColumnVisibility = null;
        updatedUi.defaultShortlistSortColumn = null;
        updatedUi.defaultShortlistSortDirection = null;
      } else if (type === 'rejected') {
        updatedUi.defaultRejectedColumnOrder = null;
        updatedUi.defaultRejectedColumnVisibility = null;
        updatedUi.defaultRejectedSortColumn = null;
        updatedUi.defaultRejectedSortDirection = null;
      } else {
        updatedUi.defaultExcelColumnOrder = null;
        updatedUi.defaultExcelColumnVisibility = null;
      }
      await axios.put(`/api/internal/org-config/${orgId}`, { ui: updatedUi });
      toast.success('Reset to defaults');
      await loadConfig();
    } catch (err) {
      toast.error('Failed to reset table layout');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-center text-sm text-gray-500">Loading layout...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Table & Export Layouts</h3>
          <p className="text-xs text-gray-500 mt-1">
            {hasOverrides ? 'Custom layout configured.' : 'Using standard platform defaults.'}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#007BFF] text-white rounded-lg hover:bg-[#0069d9]">
            <Save className="h-3.5 w-3.5" /> {saving ? 'Saving...' : 'Save Layout'}
          </button>
        </div>
      </div>

      {/* ── Candidates Tab Layout ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setActiveTab(activeTab === 'ui' ? null : 'ui')}
        >
          <div>
            <h4 className="text-sm font-semibold text-gray-800">Candidates Tab Layout</h4>
            <p className="text-xs text-gray-500 mt-1">Configure the default columns shown in the Candidates tab.</p>
          </div>
          <div className="flex items-center gap-3">
            { (rawUiConfig.defaultCandidateColumnOrder) && (
              <button 
                onClick={(e) => { e.stopPropagation(); handleResetToDefault('ui'); }} 
                disabled={saving} 
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-100"
              >
                <RefreshCw className="h-3 w-3" /> Reset
              </button>
            )}
            <svg className={`w-5 h-5 text-gray-400 transform transition-transform ${activeTab === 'ui' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {activeTab === 'ui' && (
          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
        <p className="text-sm text-gray-600 mb-2">
          Drag and drop columns to change their order in the candidates table. Click the eye icon to show or hide a column by default.
        </p>
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex items-center gap-4 text-[11px] text-gray-500">
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400"></span> Shown by default</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300"></span> Hidden by default</span>
            <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400"></span> Customised for this org</span>
          </div>
          
          {/* Candidates Sort Controls */}
          <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
            <span className="text-sm font-medium text-gray-700 w-32">Default Sort:</span>
            <select
              value={candidateSortColumn}
              onChange={(e) => setCandidateSortColumn(e.target.value)}
              className="flex-1 max-w-[200px] h-9 rounded-md border-gray-300 text-sm focus:ring-[#007BFF] focus:border-[#007BFF] bg-gray-50 border py-1.5 px-3 outline-none"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
            <button
              onClick={() => setCandidateSortDirection(d => d === 'desc' ? 'asc' : 'desc')}
              className="flex items-center justify-center h-9 w-9 border border-gray-300 rounded-md bg-gray-50 text-gray-600 hover:bg-white hover:text-[#007BFF] hover:border-[#007BFF] transition-colors"
              title={candidateSortDirection === 'desc' ? 'Descending' : 'Ascending'}
            >
              {candidateSortDirection === 'desc' ? (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
              ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
              )}
            </button>
          </div>
        </div>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="columns-list">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {order.map((colId, index) => {
                  const platformDefault = PLATFORM_DEFAULT_VISIBILITY[colId] !== undefined
                    ? PLATFORM_DEFAULT_VISIBILITY[colId]
                    : true; // custom fields default to visible
                  const isVisible = visibility[colId] !== undefined ? visibility[colId] : platformDefault;
                  const isCustom = colId.startsWith('custom_');
                  const isOverridden = visibility[colId] !== undefined && visibility[colId] !== platformDefault;
                  return (
                    <Draggable key={colId} draggableId={colId} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            snapshot.isDragging ? 'bg-blue-50 border-blue-200 shadow-md' : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div {...provided.dragHandleProps} className="text-gray-400 hover:text-gray-600">
                              <GripVertical className="h-5 w-5" />
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-800">
                                {labels[colId] || colId}
                              </span>
                              {isCustom && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800">
                                  Custom Field
                                </span>
                              )}
                              {!isVisible && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">
                                  Hidden by default
                                </span>
                              )}
                              {isVisible && !isCustom && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700">
                                  Shown by default
                                </span>
                              )}
                              {isOverridden && (
                                <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700">
                                  Customised
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => toggleVisibility(colId)}
                            className={`p-1.5 rounded-md transition-colors ${
                              isVisible ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={isVisible ? 'Hide column' : 'Show column'}
                          >
                            {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          </button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
          </div>
        )}
      </div>

      {/* ── Selected (Shortlist) Tab Layout ──────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setActiveTab(activeTab === 'shortlist' ? null : 'shortlist')}
        >
          <div>
            <h4 className="text-sm font-semibold text-gray-800">Selected Tab Layout</h4>
            <p className="text-xs text-gray-500 mt-1">Configure the default columns for the Selected (shortlisted) candidates tab. Includes Decision, Notes and Selected Date.</p>
          </div>
          <div className="flex items-center gap-3">
            {rawUiConfig.defaultShortlistColumnOrder && (
              <button
                onClick={(e) => { e.stopPropagation(); handleResetToDefault('shortlist'); }}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-100"
              >
                <RefreshCw className="h-3 w-3" /> Reset
              </button>
            )}
            <svg className={`w-5 h-5 text-gray-400 transform transition-transform ${activeTab === 'shortlist' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {activeTab === 'shortlist' && (
          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
            <p className="text-sm text-gray-600 mb-2">
              Drag and drop columns to set their default order in the Selected tab. Decision and Notes are shown here by default.
            </p>
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex items-center gap-4 text-[11px] text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400"></span> Shown by default</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300"></span> Hidden by default</span>
              </div>
              
              {/* Shortlist Sort Controls */}
              <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                <span className="text-sm font-medium text-gray-700 w-32">Default Sort:</span>
                <select
                  value={shortlistSortColumn}
                  onChange={(e) => setShortlistSortColumn(e.target.value)}
                  className="flex-1 max-w-[200px] h-9 rounded-md border-gray-300 text-sm focus:ring-[#007BFF] focus:border-[#007BFF] bg-gray-50 border py-1.5 px-3 outline-none"
                >
                  {SHORTLIST_SORT_OPTIONS.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => setShortlistSortDirection(d => d === 'desc' ? 'asc' : 'desc')}
                  className="flex items-center justify-center h-9 w-9 border border-gray-300 rounded-md bg-gray-50 text-gray-600 hover:bg-white hover:text-[#007BFF] hover:border-[#007BFF] transition-colors"
                  title={shortlistSortDirection === 'desc' ? 'Descending' : 'Ascending'}
                >
                  {shortlistSortDirection === 'desc' ? (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
                  )}
                </button>
              </div>
            </div>
            <DragDropContext onDragEnd={handleShortlistDragEnd}>
              <Droppable droppableId="shortlist-columns-list">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                    {shortlistOrder.map((colId, index) => {
                      const platformDefault = PLATFORM_DEFAULT_VISIBILITY[colId] !== undefined ? PLATFORM_DEFAULT_VISIBILITY[colId] : true;
                      const isVisible = shortlistVisibility[colId] !== undefined ? shortlistVisibility[colId] : platformDefault;
                      const isCustom = colId.startsWith('custom_');
                      return (
                        <Draggable key={colId} draggableId={`sl-${colId}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center justify-between p-3 rounded-lg border ${snapshot.isDragging ? 'bg-blue-50 border-blue-200 shadow-md' : 'bg-white border-gray-200'}`}
                            >
                              <div className="flex items-center gap-3">
                                <div {...provided.dragHandleProps} className="text-gray-400 hover:text-gray-600">
                                  <GripVertical className="h-5 w-5" />
                                </div>
                                <div>
                                  <span className="text-sm font-medium text-gray-800">{labels[colId] || colId}</span>
                                  {isCustom && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800">Custom Field</span>}
                                  {!isVisible && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">Hidden by default</span>}
                                  {isVisible && !isCustom && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700">Shown by default</span>}
                                </div>
                              </div>
                              <button
                                onClick={() => toggleShortlistVisibility(colId)}
                                className={`p-1.5 rounded-md transition-colors ${isVisible ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'}`}
                                title={isVisible ? 'Hide column' : 'Show column'}
                              >
                                {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                              </button>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>

      {/* ── Rejected Tab Layout ───────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setActiveTab(activeTab === 'rejected' ? null : 'rejected')}
        >
          <div>
            <h4 className="text-sm font-semibold text-gray-800">Rejected Tab Layout</h4>
            <p className="text-xs text-gray-500 mt-1">Configure the default columns for the Rejected candidates tab. Includes Decision and Notes.</p>
          </div>
          <div className="flex items-center gap-3">
            {rawUiConfig.defaultRejectedColumnOrder && (
              <button
                onClick={(e) => { e.stopPropagation(); handleResetToDefault('rejected'); }}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-100"
              >
                <RefreshCw className="h-3 w-3" /> Reset
              </button>
            )}
            <svg className={`w-5 h-5 text-gray-400 transform transition-transform ${activeTab === 'rejected' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {activeTab === 'rejected' && (
          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
            <p className="text-sm text-gray-600 mb-2">
              Drag and drop columns to set their default order in the Rejected tab. Decision and Notes are shown here by default.
            </p>
            <div className="flex flex-col gap-4 mb-4">
              <div className="flex items-center gap-4 text-[11px] text-gray-500">
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-400"></span> Shown by default</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300"></span> Hidden by default</span>
              </div>

              {/* Rejected Sort Controls */}
              <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                <span className="text-sm font-medium text-gray-700 w-32">Default Sort:</span>
                <select
                  value={rejectedSortColumn}
                  onChange={(e) => setRejectedSortColumn(e.target.value)}
                  className="flex-1 max-w-[200px] h-9 rounded-md border-gray-300 text-sm focus:ring-[#007BFF] focus:border-[#007BFF] bg-gray-50 border py-1.5 px-3 outline-none"
                >
                  {REJECTED_SORT_OPTIONS.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => setRejectedSortDirection(d => d === 'desc' ? 'asc' : 'desc')}
                  className="flex items-center justify-center h-9 w-9 border border-gray-300 rounded-md bg-gray-50 text-gray-600 hover:bg-white hover:text-[#007BFF] hover:border-[#007BFF] transition-colors"
                  title={rejectedSortDirection === 'desc' ? 'Descending' : 'Ascending'}
                >
                  {rejectedSortDirection === 'desc' ? (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  ) : (
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
                  )}
                </button>
              </div>
            </div>
            <DragDropContext onDragEnd={handleRejectedDragEnd}>
              <Droppable droppableId="rejected-columns-list">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                    {rejectedOrder.map((colId, index) => {
                      const platformDefault = PLATFORM_DEFAULT_VISIBILITY[colId] !== undefined ? PLATFORM_DEFAULT_VISIBILITY[colId] : true;
                      const isVisible = rejectedVisibility[colId] !== undefined ? rejectedVisibility[colId] : platformDefault;
                      const isCustom = colId.startsWith('custom_');
                      return (
                        <Draggable key={colId} draggableId={`rj-${colId}`} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`flex items-center justify-between p-3 rounded-lg border ${snapshot.isDragging ? 'bg-blue-50 border-blue-200 shadow-md' : 'bg-white border-gray-200'}`}
                            >
                              <div className="flex items-center gap-3">
                                <div {...provided.dragHandleProps} className="text-gray-400 hover:text-gray-600">
                                  <GripVertical className="h-5 w-5" />
                                </div>
                                <div>
                                  <span className="text-sm font-medium text-gray-800">{labels[colId] || colId}</span>
                                  {isCustom && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800">Custom Field</span>}
                                  {!isVisible && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-500">Hidden by default</span>}
                                  {isVisible && !isCustom && <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-700">Shown by default</span>}
                                </div>
                              </div>
                              <button
                                onClick={() => toggleRejectedVisibility(colId)}
                                className={`p-1.5 rounded-md transition-colors ${isVisible ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'}`}
                                title={isVisible ? 'Hide column' : 'Show column'}
                              >
                                {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                              </button>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setActiveTab(activeTab === 'excel' ? null : 'excel')}
        >
          <div>
            <h4 className="text-sm font-semibold text-gray-800">Excel Export Layout</h4>
            <p className="text-xs text-gray-500 mt-1">Configure the columns for the downloaded Excel files.</p>
          </div>
          <div className="flex items-center gap-3">
            { (rawUiConfig.defaultExcelColumnOrder) && (
              <button 
                onClick={(e) => { e.stopPropagation(); handleResetToDefault('excel'); }} 
                disabled={saving} 
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-100"
              >
                <RefreshCw className="h-3 w-3" /> Reset
              </button>
            )}
            <svg className={`w-5 h-5 text-gray-400 transform transition-transform ${activeTab === 'excel' ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {activeTab === 'excel' && (
          <div className="p-4 border-t border-gray-100 bg-gray-50/50">
        <p className="text-sm text-gray-600 mb-4">
          Drag and drop columns to change their order in the Excel export. Click the eye icon to show or hide a column by default.
        </p>
        <DragDropContext onDragEnd={handleExcelDragEnd}>
          <Droppable droppableId="excel-columns-list">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {excelOrder.map((colId, index) => {
                  const platformDefault = PLATFORM_DEFAULT_VISIBILITY[colId] !== undefined
                    ? PLATFORM_DEFAULT_VISIBILITY[colId]
                    : true;
                  const isVisible = excelVisibility[colId] !== undefined ? excelVisibility[colId] : platformDefault;
                  const isCustom = colId.startsWith('custom_');
                  return (
                    <Draggable key={colId} draggableId={colId} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            snapshot.isDragging ? 'bg-blue-50 border-blue-200 shadow-md' : 'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div {...provided.dragHandleProps} className="text-gray-400 hover:text-gray-600">
                              <GripVertical className="h-5 w-5" />
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-800">
                                {labels[colId] || colId}
                              </span>
                              {isCustom && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800">
                                  Custom Field
                                </span>
                              )}
                              {!isVisible && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                                  Hidden
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => toggleExcelVisibility(colId)}
                            className={`p-1.5 rounded-md transition-colors ${
                              isVisible ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={isVisible ? 'Hide column' : 'Show column'}
                          >
                            {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          </button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableLayoutAdmin;
